import type { DetailedCalculationResult } from './types';

/**
 * Input values for total on the books calculation
 */
interface TotalOnTheBooksInputs {
	currentOtb: number;
	groupBooked: number;
	groupBlocked: number;
}

/**
 * Calculation steps for total on the books
 */
interface TotalOnTheBooksSteps {
	transientAndGroupBookings: { value: number; description: string };
	groupBlockedRooms: { value: number; description: string };
	total: { value: number; description: string };
}

/**
 * Calculate the total number of rooms that are on the books.
 * Note: current_otb already includes transient bookings AND group_booked,
 * so we only need to add group_blocked to get the total.
 */
export const calculateTotalOnTheBooks = (
	currentOtb: number,
	groupBooked: number,
	groupBlocked: number,
): DetailedCalculationResult<
	number,
	TotalOnTheBooksInputs,
	TotalOnTheBooksSteps
> => {
	// Ensure values are numbers and default to 0 if undefined
	const otb = Number(currentOtb) || 0;
	const booked = Number(groupBooked) || 0; // Included for information only
	const blocked = Number(groupBlocked) || 0;

	// Calculate the total (don't add groupBooked again since it's already in currentOtb)
	const total = otb + blocked;

	return {
		value: total,
		details: {
			inputValues: {
				currentOtb: otb,
				groupBooked: booked,
				groupBlocked: blocked,
			},
			calculations: {
				transientAndGroupBookings: {
					value: otb,
					description:
						'Current OTB (already includes transient bookings AND group booked rooms)',
				},
				groupBlockedRooms: {
					value: blocked,
					description:
						'Group rooms that are held/blocked but have no reservations yet',
				},
				total: {
					value: total,
					description:
						'Total inventory on the books (current OTB + group blocked)',
				},
			},
		},
	};
};
